import React from 'react'
import { useState } from 'react'

import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'

import features from 'common/college-features'
import permissions from 'common/system-permissions'
import { useOpenModalForCreate } from 'components/ComposeOutreachMessageButton/Context'
import { ComposeContextWrapper } from 'components/ComposeOutreachMessageButton/ContextWrappers'
import { getDefaultMessage } from 'components/ComposeOutreachMessageButton/services/message-service'
import { ENGAGEMENT_STAGES } from 'components/SegmentSelector/components/EngagementStage'
import {
  AUTOMATED_TRIGGER_CONFIGS,
  DEFAULT_AUTOMATED_TRIGGER,
  ONE_TIME_TRIGGER,
} from 'components/SegmentSelector/outreach-message-trigger-constants'
import {
  getIsOneTimeMessage,
  setAcademicFocus,
  setEngagementStage,
  setInterest,
} from 'components/SegmentSelector/segment-service'
import { isFeatureEnabled } from 'store/college'
import { hasPermission } from 'store/user'

import MessageCreatedNotification from '../MessageCreatedNotification'
import SelectMessageTypeModal from '../SelectMessageTypeModal'
import DashTable from './DashTable'

const getEngagementStages = (filter) => {
  switch (filter) {
    default:
    case 'all':
      return ENGAGEMENT_STAGES
    case 'following':
      return ['Following']
    case 'applying':
      return ['Applying']
    case 'applied':
      return ['Applied']
  }
}

const getSegmentUpdater = (label) => {
  switch (label) {
    case 'Interest':
      return setInterest
    case 'Academic Focus':
      return setAcademicFocus
    default:
      console.error('Could not find segment update function for ', label)
      return (message) => message
  }
}

const getTrigger = (filter, engagementStages) => {
  if (filter === 'all' || engagementStages.length !== 1) return null

  const trigger = AUTOMATED_TRIGGER_CONFIGS.find((t) => t.label === engagementStages[0])
  return trigger && trigger.value
}

export const MessageTableComponent = ({ data, error, history, loading, nameLabel, tableName }) => {
  const [selectedRow, setSelectedRow] = useState(null)
  const [selectedFilter, setSelectedFilter] = useState('all')
  const [showSelectMessageTypeModal, setShowSelectMessageTypeModal] = useState(false)
  const showModal = useOpenModalForCreate()

  const handleNext = (trigger) => {
    setShowSelectMessageTypeModal(false)

    const isOneTimeMessage = getIsOneTimeMessage(trigger)
    const message = getDefaultMessage(isOneTimeMessage ? ONE_TIME_TRIGGER : DEFAULT_AUTOMATED_TRIGGER)
    const engagementStages = getEngagementStages(selectedFilter)

    if (isOneTimeMessage) {
      message.Segment = setEngagementStage(message.Segment, engagementStages)
    } else {
      message.Trigger = getTrigger(selectedFilter, engagementStages)
    }

    const segmentUpdater = getSegmentUpdater(nameLabel)
    message.Segment = segmentUpdater(message.Segment, [selectedRow.name])

    //  Executing this after the render has completed due to both the
    //  <Modal /> components futzing with the body's style and attempting
    //  to restore it to the original state.  Without the setTimeout,
    //  the first modal component will remove the page's scrollbar by
    //  setting `overflow: none`, then opening a second modal will capture
    //  the body's style (including the `overflow: none`, do its thing,
    //  then resset the body's stil to its "original" state (ie: with
    //  no scrollbar because the menu was open)
    setTimeout(() => showModal(message), 0)
  }

  const hasFeature = useSelector((store) => isFeatureEnabled(store, features.SUPPORTS_STUDENT_OUTREACH))
  const hasPerm = useSelector((store) => hasPermission(store, permissions.MANAGE_COLLEGE_OUTREACH_MESSAGES))

  const actions = () => {
    const a = [
      {
        label: 'Send Message',
        onClick: (row) => {
          setSelectedRow(row)
          setShowSelectMessageTypeModal(true)
        },
      },
    ]
    if (hasFeature && hasPerm) {
      return [
        ...a,
        {
          label: 'Manage Messages',
          onClick: () => history.push(`${import.meta.env.BASE_URL}/engage/messaging`),
        },
      ]
    }
    return a
  }

  return (
    <React.Fragment>
      <SelectMessageTypeModal
        show={showSelectMessageTypeModal}
        onHide={() => setShowSelectMessageTypeModal(false)}
        onNext={handleNext}
      />
      <DashTable
        tableName={tableName}
        columns={[
          { id: 'name', label: nameLabel, enableSort: false, header: true },
          /* These are just to cover our asses and aren't normal use cases */
          {
            id: 'count',
            label: 'Students',
            enableSort: true,
            align: 'right',
            format: (value) => (value ? value.toLocaleString() : 0),
          },
          {
            id: 'message_count',
            label: 'Messages',
            enableSort: true,
            align: 'right',
            format: (value) => (value ? value.toLocaleString() : 0),
          },
        ]}
        groups={[
          { id: 'all', label: 'All Interested Students', rows: data.all },
          { id: 'following', label: 'Following', rows: data.following },
          { id: 'applying', label: 'Applying', rows: data.applying },
          { id: 'applied', label: 'Applied', rows: data.applied },
        ]}
        actions={actions()}
        defaultFilter={selectedFilter}
        loading={loading}
        error={error}
        onFilterChange={setSelectedFilter}
      />
    </React.Fragment>
  )
}

const groupPropType = PropTypes.arrayOf(
  PropTypes.shape({
    name: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    message_count: PropTypes.number,
  }).isRequired,
).isRequired

MessageTableComponent.propTypes = {
  data: PropTypes.shape({
    all: groupPropType,
    following: groupPropType,
    applying: groupPropType,
    applied: groupPropType,
  }).isRequired,
  error: PropTypes.bool.isRequired,
  history: PropTypes.any,
  loading: PropTypes.bool.isRequired,
  nameLabel: PropTypes.string.isRequired,
  tableName: PropTypes.string.isRequired,
}

const MessageTable = (props) => {
  const [showNotification, setShowNotification] = useState(false)

  return (
    <ComposeContextWrapper onSave={() => setShowNotification(true)}>
      <MessageTableComponent {...props} />
      <MessageCreatedNotification
        onDismiss={() => setShowNotification(false)}
        show={showNotification}
        fullWidth={false}
      />
    </ComposeContextWrapper>
  )
}

MessageTable.propTypes = {
  onMessageSent: PropTypes.func.isRequired,
}

export default withRouter(MessageTable)
